import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { guidePageSchema } from '../../utils/schema';

const ARTICLE_SCHEMA = {
  '@type': 'Article',
  headline: 'When Does My Arizona Real Estate License Expire?',
  description: 'How to find your exact Arizona license expiration date, what the two-year cycle means, and a renewal timeline that avoids the $125 late fee.',
  datePublished: '2026-06-01',
  dateModified: '2026-06-10',
  author: { '@type': 'Organization', name: 'Steady Education' },
  publisher: { '@type': 'EducationalOrganization', name: 'Steady Education', url: 'https://steadyeducation.com' },
};

const RELATED = [
  { url: '/arizona/how-to-renew', title: 'How to Renew, Step by Step', desc: 'The full renewal in five steps: CE, Licensee Login, Renew, pay.' },
  { url: '/arizona-license-expired-late-renewal', title: 'Renewing Late: Fees & Reinstatement', desc: 'What happens after expiration and how to fix it within the one-year window.' },
  { url: '/how-to-submit-ce-to-adre', title: 'How to Submit CE to ADRE', desc: 'Your school doesn\'t report your courses — you do.' },
  { url: '/arizona-real-estate-ce-requirements', title: 'Arizona CE Requirements 2026', desc: 'The complete 24-hour breakdown.' },
];

export default function WhenExpiresPage() {
  useSEO({
    title: 'When Does My AZ Real Estate License Expire? How to Check',
    description: 'How to find your exact Arizona license expiration date, what the two-year cycle means, and a renewal timeline that avoids the $125 late fee.',
    url: canonicalUrl('/when-does-my-arizona-license-expire'),
    jsonLd: guidePageSchema(ARTICLE_SCHEMA, {
      title: 'When Does My License Expire?',
      url: canonicalUrl('/when-does-my-arizona-license-expire'),
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="License Expiration"
      title="When Does My Arizona Real Estate License Expire? (And How to Check in 60 Seconds)"
      subtitle="Arizona real estate licenses renew every two years, with your expiration tied to your original issue date — not a fixed statewide deadline. That means your renewal date is personal, and it's easy to lose track of."
      relatedGuides={RELATED}
    >
      <h2>Check your exact date two ways</h2>
      <ol>
        <li><strong>ADRE Licensee Login</strong> (ptl.az.gov/dre) — your license record shows the expiration date plus your CE progress by category. This is the one to bookmark: it also shows which course categories you still owe.</li>
        <li><strong>ADRE Public Database</strong> (services.azre.gov/publicdatabase) — anyone can look up any licensee's status and expiration. No login needed. (Your clients and your broker can see this too.)</li>
      </ol>

      <h2>Your renewal timeline, working backward from expiration</h2>
      <ul>
        <li><strong>90 days out:</strong> Check your Licensee Login for category deficiencies. Start CE if you haven't.</li>
        <li><strong>30 days out:</strong> All 24 hours done and submitted. Remember: ADRE caps you at 9 CE hours per day, so you need at least 3 days of runway — and certificates must be entered in the portal yourself; schools don't report them for you.</li>
        <li><strong>Renewal month:</strong> Renew online, pay the $60 fee, done.</li>
        <li><strong>Day after expiration:</strong> Your license is inactive. You cannot practice, and the fee more than doubles to <strong>$125</strong>.</li>
      </ul>

      <h2>Set it and forget it</h2>
      <p>Put two calendar reminders in your phone right now — one at 90 days, one at 30. The number one reason Arizona agents pay the late fee isn't laziness; it's that a two-year cycle is long enough to forget entirely.</p>

      <div style={inlineCta}>
        <strong>Need your hours?</strong> All 24, online, $49.99, with certificates issued instantly.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>Get the package →</Link>
        </div>
      </div>
    </ResourceLayout>
  );
}

const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
